import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import VirtualKeyboard from './VirtualKeyboard';

const ContactFormScreen = ({ selectedRating, onSubmit }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [timeLeft, setTimeLeft] = useState(90);
  const [timerActive, setTimerActive] = useState(true);

  const handleKeyPress = (key) => {
    if (phoneNumber.length < 15) { // Limit phone number length
      setPhoneNumber(prev => prev + key);
    }
  };

  const handleBackspace = () => {
    setPhoneNumber(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPhoneNumber('');
  };

  const handleSubmit = () => {
    if (phoneNumber.length >= 10) { // Basic validation
      setTimerActive(false);
      onSubmit(phoneNumber, 'submitted');
    }
  };

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      // Auto-skip when timer reaches 0
      setTimerActive(false);
      onSubmit('', 'auto-timeout'); // Submit with empty phone number and auto-timeout action
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft, onSubmit]);

  const handleSkip = () => {
    setTimerActive(false);
    onSubmit('', 'skipped'); // Submit with empty phone number and skipped action
  };


  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, delay: 0.2 }
    }
  };

  const getEmojiForRating = (rating) => {
    switch (rating) {
      case 'dissatisfied':
        return '😔';
      case 'very-dissatisfied':
        return '😠';
      default:
        return '😔';
    }
  };

  const getMessageForRating = (rating) => {
    switch (rating) {
      case 'dissatisfied':
        return "We're sorry to hear you had a disappointing experience.";
      case 'very-dissatisfied':
        return "We're deeply sorry for the poor experience you had.";
      default:
        return "We're sorry to hear about your experience.";
    }
  };

  return (
    <motion.div 
      className="contact-form-screen"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="contact-form-layout">
        {/* Left Column - Content */}
        <div className="contact-form-content">
          <motion.div 
            className="contact-emoji"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            {getEmojiForRating(selectedRating)}
          </motion.div>
          
          <motion.h2
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            We'd like to make this right
          </motion.h2>
          
          <motion.p
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            {getMessageForRating(selectedRating)}
          </motion.p>
          
          <motion.p
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            Please provide your phone number so our management team can contact you directly to resolve this issue.
          </motion.p>
          
          <motion.div 
            className="timer-display"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <p className="timer-label">Auto-skip in {timeLeft} seconds</p>
          </motion.div>
          
          <motion.div 
            className="phone-input-container"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="phone-display">
              {phoneNumber || 'Enter your phone number'}
            </div>
            <div className="phone-hint">
              {phoneNumber.length < 10 ? 'Please enter at least 10 digits' : '✓ Phone number looks good'}
            </div>
          </motion.div>
          
          <motion.div 
            className="contact-actions"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.button 
              className="skip-button"
              onClick={handleSkip}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Skip
            </motion.button>
          </motion.div>
        </div>

        {/* Right Column - Keypad */}
        <motion.div 
          className="contact-keypad-container"
          variants={contentVariants}
          initial="hidden"
          animate="visible"
        >
          <VirtualKeyboard
            onKeyPress={handleKeyPress}
            onBackspace={handleBackspace}
            onSubmit={handleSubmit}
            onClear={handleClear}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ContactFormScreen;
